import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { api } from "../services/api";

// ── Palette ──────────────────────────────────────────────────

const COLORS = [
  "#2196f3", "#4caf50", "#ff9800", "#e91e63", "#9c27b0",
  "#00bcd4", "#ff5722", "#795548", "#607d8b", "#8bc34a",
  "#3f51b5", "#f44336", "#009688", "#ffc107", "#673ab7",
];

// ── Currency formatter ──────────────────────────────────────

const fmtCurrency = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);

const fmtCurrencyDollars = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

const fmtMonthLabel = (ym: string) => {
  const [y, m] = ym.split("-");
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleString("default", { month: "short", year: "2-digit" });
};

// ── Shared tooltip style ────────────────────────────────────

const tooltipStyle = {
  backgroundColor: "var(--template-palette-background-paper, #fff)",
  color: "var(--template-palette-text-primary, #000)",
  borderRadius: 8,
  border: "1px solid var(--template-palette-divider, #e0e0e0)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  padding: "8px 12px",
};

// ── Section wrapper ─────────────────────────────────────────

function ChartSection({
  title,
  subtitle,
  children,
  minHeight = 340,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  minHeight?: number;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        minHeight,
      }}
    >
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

// ── Main page ───────────────────────────────────────────────

export default function ReportsPage() {
  // Controls
  const [monthRange, setMonthRange] = useState(12);

  // Data
  const [monthlySummary, setMonthlySummary] = useState<any[] | null>(null);
  const [categorySpending, setCategorySpending] = useState<any[] | null>(null);
  const [categoryTrends, setCategoryTrends] = useState<{
    categories: string[];
    series: any[];
  } | null>(null);
  const [topMerchants, setTopMerchants] = useState<any[] | null>(null);
  const [netWorthHistory, setNetWorthHistory] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pie drill-down breadcrumb stack: empty = top-level, array = drill path
  // Each entry stores the category's id, name, and its resolved children data
  const [pieDrillStack, setPieDrillStack] = useState<
    Array<{
      id: string;
      name: string;
      subcategories: Array<{ id: string; name: string; totalCents: number; txCount: number; hasChildren: boolean }>;
    }>
  >([]);
  const [pieDrillLoading, setPieDrillLoading] = useState(false);

  // Category drilldown section state
  const [drilldownCategoryId, setDrilldownCategoryId] = useState<string | null>(null);
  const [drilldownData, setDrilldownData] = useState<{
    parentName: string;
    subcategories: Array<{ id: string; name: string }>;
    series: any[];
  } | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // All categories from the tree (for the drilldown picker)
  const [allCategories, setAllCategories] = useState<Array<{ id: string; name: string; parentId?: string | null; kind?: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPieDrillStack([]);

    Promise.all([
      api.getReportMonthlySummary(monthRange),
      api.getReportCategorySpending(monthRange),
      api.getReportCategoryTrends(monthRange, 8),
      api.getReportTopMerchants(monthRange, 10),
      api.getNetWorthHistory(monthRange),
      api.getCategories(),
    ])
      .then(([summary, catSpend, catTrends, merchants, nw, cats]) => {
        if (cancelled) return;
        setMonthlySummary(summary.months);
        setCategorySpending(catSpend.categories);
        setCategoryTrends({
          categories: catTrends.categories,
          series: catTrends.series,
        });
        setTopMerchants(merchants.merchants);
        setNetWorthHistory(nw.snapshots);
        setAllCategories(cats.categories);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? "Failed to load reports");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [monthRange]);

  // Fetch drilldown data when category selection changes
  useEffect(() => {
    if (!drilldownCategoryId) {
      setDrilldownData(null);
      return;
    }
    let cancelled = false;
    setDrilldownLoading(true);
    api
      .getReportCategoryDrilldown(drilldownCategoryId, monthRange)
      .then((resp) => {
        if (cancelled) return;
        setDrilldownData({
          parentName: resp.parentName,
          subcategories: resp.subcategories,
          series: resp.series,
        });
      })
      .catch(() => {
        if (!cancelled) setDrilldownData(null);
      })
      .finally(() => {
        if (!cancelled) setDrilldownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drilldownCategoryId, monthRange]);

  // ── Derived data ────────────────────────────────────────────

  // Income vs Expenses bar chart data
  const incomeVsExpenseData = useMemo(() => {
    if (!monthlySummary) return [];
    return monthlySummary.map((m) => ({
      month: fmtMonthLabel(m.month),
      Income: m.incomeCents / 100,
      Expenses: m.expenseCents / 100,
    }));
  }, [monthlySummary]);

  // Savings rate line chart data
  const savingsRateData = useMemo(() => {
    if (!monthlySummary) return [];
    return monthlySummary.map((m) => {
      const income = m.incomeCents / 100;
      const expense = m.expenseCents / 100;
      const saved = income - expense;
      const rate = income > 0 ? saved / income : 0;
      return {
        month: fmtMonthLabel(m.month),
        "Savings Rate": parseFloat((rate * 100).toFixed(1)),
        Saved: parseFloat(saved.toFixed(2)),
      };
    });
  }, [monthlySummary]);

  // Category spending pie — top-level OR subcategory drill-in
  const categoryPieData = useMemo(() => {
    if (pieDrillStack.length > 0) {
      // Showing subcategories of the deepest drilled-in parent
      const current = pieDrillStack[pieDrillStack.length - 1];
      const subs = current.subcategories;
      if (!subs.length) return [];
      const total = subs.reduce((s: number, c) => s + c.totalCents, 0);
      return subs.map((c) => ({
        id: c.id,
        name: c.name,
        value: c.totalCents / 100,
        pct: total > 0 ? c.totalCents / total : 0,
        hasSubcategories: c.hasChildren,
      }));
    }
    if (!categorySpending) return [];
    const total = categorySpending.reduce((s: number, c: any) => s + c.totalCents, 0);
    const sorted = [...categorySpending].sort(
      (a: any, b: any) => b.totalCents - a.totalCents
    );
    const top = sorted.slice(0, 8);
    const otherCents = sorted.slice(8).reduce((s: number, c: any) => s + c.totalCents, 0);
    const items = top.map((c: any) => ({
      id: c.id,
      name: c.name,
      value: c.totalCents / 100,
      pct: total > 0 ? c.totalCents / total : 0,
      hasSubcategories: (c.subcategories?.length ?? 0) > 0,
    }));
    if (otherCents > 0) {
      items.push({
        id: "__other__",
        name: "Other",
        value: otherCents / 100,
        pct: total > 0 ? otherCents / total : 0,
        hasSubcategories: false,
      });
    }
    return items;
  }, [categorySpending, pieDrillStack]);

  // Category options for the drilldown picker — built from the full category tree
  const categoryPickerOptions = useMemo(() => {
    if (!allCategories.length) return [];
    // Find which category IDs are someone's parent
    const parentIds = new Set<string>();
    for (const c of allCategories) {
      if (c.parentId) parentIds.add(c.parentId);
    }
    // Only offer categories that have at least one child
    return allCategories
      .filter((c) => parentIds.has(c.id))
      .map((c) => ({ id: c.id, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCategories]);

  // Drilldown chart data
  const drilldownChartData = useMemo(() => {
    if (!drilldownData) return { subcategories: [] as string[], series: [] as any[] };
    return {
      subcategories: drilldownData.subcategories.map((s) => s.name),
      series: drilldownData.series.map((point) => ({
        ...point,
        month: fmtMonthLabel(point.month),
      })),
    };
  }, [drilldownData]);

  // Handle clicking a pie slice — drill into subcategories at any depth
  const handlePieClick = useCallback(
    async (data: any) => {
      if (pieDrillLoading) return;
      const entry = categoryPieData.find((c: any) => c.name === data.name);
      if (!entry || !(entry as any).hasSubcategories) return;
      const entryId = (entry as any).id;

      setPieDrillLoading(true);
      try {
        const resp = await api.getReportSubcategorySpending(entryId, monthRange);
        if (resp.subcategories.length > 0) {
          setPieDrillStack((prev) => [
            ...prev,
            {
              id: entryId,
              name: entry.name,
              subcategories: resp.subcategories,
            },
          ]);
        }
      } catch (err) {
        console.error("Failed to load subcategory spending:", err);
      } finally {
        setPieDrillLoading(false);
      }
    },
    [categoryPieData, pieDrillLoading, monthRange]
  );

  // Category trends line chart data (already shaped by server)
  const categoryTrendData = useMemo(() => {
    if (!categoryTrends) return { categories: [] as string[], series: [] as any[] };
    return {
      categories: categoryTrends.categories,
      series: categoryTrends.series.map((point) => ({
        ...point,
        month: fmtMonthLabel(point.month),
      })),
    };
  }, [categoryTrends]);

  // Top merchants bar data
  const merchantBarData = useMemo(() => {
    if (!topMerchants) return [];
    return topMerchants.map((m) => ({
      name: m.name.length > 25 ? m.name.slice(0, 23) + "…" : m.name,
      fullName: m.name,
      Amount: m.totalCents / 100,
      Transactions: m.txCount,
    }));
  }, [topMerchants]);

  // Net worth data
  const netWorthData = useMemo(() => {
    if (!netWorthHistory) return [];
    return netWorthHistory.map((s: any) => {
      const d = new Date(s.date);
      return {
        month: d.toLocaleString("default", { month: "short", year: "2-digit" }),
        "Net Worth": s.netWorthCents / 100,
        Assets: s.totalAssetsCents / 100,
        Liabilities: Math.abs(s.totalLiabilitiesCents) / 100,
      };
    });
  }, [netWorthHistory]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!monthlySummary || monthlySummary.length === 0) return null;
    const totalIncome = monthlySummary.reduce((s, m) => s + m.incomeCents, 0);
    const totalExpense = monthlySummary.reduce((s, m) => s + m.expenseCents, 0);
    const totalTx = monthlySummary.reduce((s, m) => s + m.transactionCount, 0);
    const avgMonthlyExpense = totalExpense / monthlySummary.length;
    const overallSavingsRate =
      totalIncome > 0 ? (totalIncome - totalExpense) / totalIncome : 0;
    return {
      totalIncome,
      totalExpense,
      totalTx,
      avgMonthlyExpense,
      overallSavingsRate,
      netSaved: totalIncome - totalExpense,
    };
  }, [monthlySummary]);

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          textAlign: "center",
          mt: 4,
          px: { xs: 2, md: 3 },
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        px: { xs: 2, md: 3 },
        py: 3,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", pb: { xs: 3, md: 5 } }}>
      {/* Page header + controls */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" fontWeight={700}>
          Reports
        </Typography>
        <ToggleButtonGroup
          value={monthRange}
          exclusive
          onChange={(_, v) => v && setMonthRange(v)}
          size="small"
        >
          <ToggleButton value={3}>3M</ToggleButton>
          <ToggleButton value={6}>6M</ToggleButton>
          <ToggleButton value={12}>1Y</ToggleButton>
          <ToggleButton value={24}>2Y</ToggleButton>
          <ToggleButton value={60}>5Y</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* ── Summary stat cards ── */}
      {summaryStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            {
              label: "Total Income",
              value: fmtCurrency(summaryStats.totalIncome),
              color: "#4caf50",
            },
            {
              label: "Total Spending",
              value: fmtCurrency(summaryStats.totalExpense),
              color: "#f44336",
            },
            {
              label: "Net Saved",
              value: fmtCurrency(summaryStats.netSaved),
              color: summaryStats.netSaved >= 0 ? "#4caf50" : "#f44336",
            },
            {
              label: "Savings Rate",
              value: fmtPct(summaryStats.overallSavingsRate),
              color:
                summaryStats.overallSavingsRate >= 0.2
                  ? "#4caf50"
                  : summaryStats.overallSavingsRate >= 0
                  ? "#ff9800"
                  : "#f44336",
            },
            {
              label: "Avg Monthly Spend",
              value: fmtCurrency(summaryStats.avgMonthlyExpense),
              color: "#2196f3",
            },
            {
              label: "Transactions",
              value: summaryStats.totalTx.toLocaleString(),
              color: "#9c27b0",
            },
          ].map((stat) => (
            <Grid key={stat.label} size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: stat.color, mt: 0.5 }}
                >
                  {stat.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* ── 1. Income vs Expenses ── */}
        <Grid size={{ xs: 12 }}>
          <ChartSection
            title="Income vs Spending"
            subtitle="Monthly comparison of money in vs money out"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={incomeVsExpenseData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => fmtCurrencyDollars(v)} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number) => fmtCurrencyDollars(value)}
                />
                <Legend />
                <Bar dataKey="Income" fill="#4caf50" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="#f44336" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartSection>
        </Grid>

        {/* ── 2. Savings Rate ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartSection
            title="Savings Rate"
            subtitle="Percentage of income saved each month"
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart
                data={savingsRateData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 12 }}
                  domain={["auto", "auto"]}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number, name: string) =>
                    name === "Savings Rate"
                      ? `${value.toFixed(1)}%`
                      : fmtCurrencyDollars(value)
                  }
                />
                <Area
                  type="monotone"
                  dataKey="Savings Rate"
                  stroke="#4caf50"
                  fill="url(#savingsGrad)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartSection>
        </Grid>

        {/* ── 3. Net Worth ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartSection
            title="Net Worth Over Time"
            subtitle="Assets minus liabilities"
          >
            {netWorthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={netWorthData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196f3" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2196f3" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tickFormatter={(v) => fmtCurrencyDollars(v)}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => fmtCurrencyDollars(v)}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Net Worth"
                    stroke="#2196f3"
                    fill="url(#nwGrad)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Assets"
                    stroke="#4caf50"
                    strokeDasharray="5 5"
                    dot={false}
                    strokeWidth={1.5}
                  />
                  <Line
                    type="monotone"
                    dataKey="Liabilities"
                    stroke="#f44336"
                    strokeDasharray="5 5"
                    dot={false}
                    strokeWidth={1.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No net worth snapshots yet.
              </Typography>
            )}
          </ChartSection>
        </Grid>

        {/* ── 4. Spending by Category (drillable pie) ── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <ChartSection
            title="Spending by Category"
            subtitle={`Where your money went (last ${monthRange} months)`}
          >
            {/* Breadcrumb for drill-in navigation */}
            {pieDrillStack.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setPieDrillStack((prev) =>
                      prev.length <= 1 ? [] : prev.slice(0, -1)
                    )
                  }
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Breadcrumbs separator="›" aria-label="breadcrumb">
                  <Link
                    component="button"
                    underline="hover"
                    color="text.secondary"
                    variant="body2"
                    onClick={() => setPieDrillStack([])}
                  >
                    All Categories
                  </Link>
                  {pieDrillStack.map((level, idx) =>
                    idx < pieDrillStack.length - 1 ? (
                      <Link
                        key={level.id}
                        component="button"
                        underline="hover"
                        color="text.secondary"
                        variant="body2"
                        onClick={() => setPieDrillStack((prev) => prev.slice(0, idx + 1))}
                      >
                        {level.name}
                      </Link>
                    ) : (
                      <Typography key={level.id} variant="body2" fontWeight={600}>
                        {level.name}
                      </Typography>
                    )
                  )}
                </Breadcrumbs>
              </Stack>
            )}
            {pieDrillLoading ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                Loading…
              </Typography>
            ) : categoryPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={categoryPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={2}
                    dataKey="value"
                    label={(props: any) => {
                      const { name } = props;
                      const entry = categoryPieData.find((c: any) => c.name === name);
                      const pctStr = entry ? `${(entry.pct * 100).toFixed(0)}%` : "";
                      return `${name} (${pctStr})`;
                    }}
                    labelLine={{ strokeWidth: 1 }}
                    onClick={handlePieClick}
                    style={{ cursor: "pointer" }}
                  >
                    {categoryPieData.map((_: any, i: number) => (
                      <Cell
                        key={i}
                        fill={COLORS[i % COLORS.length]}
                        stroke="none"
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => fmtCurrencyDollars(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No categorized spending data found.
              </Typography>
            )}
            {categoryPieData.some((c: any) => c.hasSubcategories) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                Click a category to drill deeper
              </Typography>
            )}
          </ChartSection>
        </Grid>

        {/* ── 5. Category Spending Trends ── */}
        <Grid size={{ xs: 12, md: 7 }}>
          <ChartSection
            title="Category Spending Trends"
            subtitle="How your top expense categories change month to month"
          >
            {categoryTrendData.series.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={categoryTrendData.series}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tickFormatter={(v) => fmtCurrencyDollars(v)}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => fmtCurrencyDollars(v)}
                  />
                  <Legend />
                  {categoryTrendData.categories.map((cat, i) => (
                    <Line
                      key={cat}
                      type="monotone"
                      dataKey={cat}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                  {categoryTrendData.series.some((s) => s.Other != null) && (
                    <Line
                      type="monotone"
                      dataKey="Other"
                      stroke="#bdbdbd"
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No category trend data found.
              </Typography>
            )}
          </ChartSection>
        </Grid>

        {/* ── 6. Category Drilldown — subcategory spending over time ── */}
        <Grid size={{ xs: 12 }}>
          <ChartSection
            title="Category Drilldown"
            subtitle="Pick a category to see how its subcategories changed month to month"
            minHeight={420}
          >
            <Autocomplete
              options={categoryPickerOptions}
              getOptionLabel={(o) => o.label}
              value={categoryPickerOptions.find((o) => o.id === drilldownCategoryId) ?? null}
              onChange={(_, val) => setDrilldownCategoryId(val?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} label="Select a category" size="small" />
              )}
              sx={{ maxWidth: 360, mb: 2 }}
            />
            {drilldownLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!drilldownLoading && drilldownData && drilldownChartData.series.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart
                  data={drilldownChartData.series}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <defs>
                    {drilldownChartData.subcategories.map((name, i) => (
                      <linearGradient key={name} id={`drillGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.05} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => fmtCurrencyDollars(v)} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => fmtCurrencyDollars(v)}
                  />
                  <Legend />
                  {drilldownChartData.subcategories.map((name, i) => (
                    <Area
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stackId="1"
                      stroke={COLORS[i % COLORS.length]}
                      fill={`url(#drillGrad${i})`}
                      strokeWidth={2}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              !drilldownLoading &&
              drilldownCategoryId && (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No subcategory data found for this category.
                </Typography>
              )
            )}
            {!drilldownCategoryId && !drilldownLoading && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                Select a category above to see how its subcategories changed over time.
              </Typography>
            )}
          </ChartSection>
        </Grid>

        {/* ── 7. Top Merchants ── */}
        <Grid size={{ xs: 12 }}>
          <ChartSection
            title="Top Merchants"
            subtitle={`Your biggest spending destinations (last ${monthRange} months)`}
          >
            {merchantBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(280, merchantBarData.length * 36)}>
                <BarChart
                  data={merchantBarData}
                  layout="vertical"
                  margin={{ top: 8, right: 40, left: 16, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => fmtCurrencyDollars(v)}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={160}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number, name: string) =>
                      name === "Amount"
                        ? fmtCurrencyDollars(v)
                        : v
                    }
                    labelFormatter={(label) => {
                      const item = merchantBarData.find((m) => m.name === label);
                      return item?.fullName ?? label;
                    }}
                  />
                  <Bar
                    dataKey="Amount"
                    fill="#2196f3"
                    radius={[0, 4, 4, 0]}
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No merchant spending data found.
              </Typography>
            )}
          </ChartSection>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}
